import { useAppStore } from 'App/store';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { fieldTypeToComponentMap } from 'shared/components/FormV2/Base';
import { EntityType } from 'shared/constants/entityTypes';
import { AddlFieldType } from 'shared/constants/fields';
import { useFieldsByDivision } from 'shared/hooks/api/fields';
import { isNonArrayObject, isNotEmpty, pick } from 'shared/utils/functions';
import Loading from '../shared/layout/Loading';

let moduleStore = null;

/**
 * Context provider for field management and form configuration.
 * Primarily used for `<Form>` and provides centralized utilities for:
 * - Field configuration retrieval (global and division-specific).
 * - Default value generation.
 * - Data transformation between client/server formats.
 * - Form field rendering.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children
 * @returns {JSX.Element}
 */
function FieldProvider({ children }) {
  const { appState } = useAppStore();
  const { divisionId: activeDivisionId, globalDivisionId } = appState;
  const { data: fieldConfig, isLoading } = useFieldsByDivision();

  /**
   * Retrieve field configurations for a Division.
   * @param {import('../shared/components/FormV2/types').GetFieldsOptions} options
   * @returns {Array<Object>} Array of field configurations.
   */
  const getFields = useCallback(
    ({
      entityType,
      useGlobal = false,
      showAllFields = false,
      divisionId = activeDivisionId,
      fieldsToShow = [],
      fieldsToMerge = [],
    }) => {
      const isCustomerOrContact = [
        // The LIMS `order_request_field` toggle is not available
        // for these entities. So show all fields by default.
        EntityType.CUSTOMER,
        EntityType.INVOICE,
        EntityType.CONTACT,
      ].includes(entityType);
      const shouldShowAllFields = isCustomerOrContact || showAllFields;
      const existingFields = get(
        fieldConfig,
        [useGlobal ? globalDivisionId : divisionId, 'fields', entityType],
        []
      )
        .filter((field) => shouldShowAllFields || field?.orderRequestField)
        .filter((field) => field.fieldType !== AddlFieldType.DUMMY)
        .map((field) => {
          if (isEmpty(fieldsToMerge)) return field;
          const fieldToMerge = fieldsToMerge.find((f) => f.name === field.name);
          return { ...field, ...fieldToMerge };
        });
      const newFields = fieldsToMerge.filter(
        (f) => !existingFields.map((f) => f.name).includes(f.name)
      );

      return [...existingFields, ...newFields].filter(
        (field) =>
          isEmpty(fieldsToShow) || fieldsToShow.includes(field.name)
      );
    },
    [fieldConfig, activeDivisionId, globalDivisionId]
  );

  /**
   * Retrieve a list of attachment entities for a Division.
   * @param {import('../shared/components/FormV2/types').GetAttachmentEntitiesOptions} options
   * @returns {Array<Object>} Array of entity types.
   */
  const getAttachmentEntities = useCallback(
    ({ divisionId = activeDivisionId, useGlobal = false }) =>
      get(
        fieldConfig,
        [useGlobal ? globalDivisionId : divisionId, 'attachmentEntities'],
        []
      ),
    [activeDivisionId, fieldConfig, globalDivisionId]
  );

  /**
   * Generate a default value object for passing into react-hook-form `useForm()`
   * when`<Form>` is in Create Mode. Default values set in LIMS CP Settings will be applied.
   * @param {import('../shared/components/FormV2/types').GetDefaultValuesOptions} options
   * @returns {import('react-hook-form').DefaultValues<Object>} The default values object.
   */
  const getDefaultValues = useCallback(
    ({ entity, ...fieldsOptions }) => {
      const isEditing = !isEmpty(entity);
      return getFields(fieldsOptions).reduce((acc, field) => {
        const FormComponent = fieldTypeToComponentMap[field.fieldType];
        const defaultValue = isEditing
          ? entity[field.name]
          : field.portalDefaultValue;
        return {
          ...acc,
          [field.name]:
            isNil(defaultValue) && !field.fieldTypeEntityReference
              ? // Note: react-hook-form prefers empty defaults to be empty string.
                // https://react-hook-form.com/docs/useform#defaultValues
                ''
              : FormComponent.transform.defaultValue(defaultValue, {
                  field: { ...field, entityType: fieldsOptions.entityType },
                  payload: entity,
                }),
        };
      }, {});
    },
    [getFields]
  );

  /**
   * Transform entity fields between different modes (server/client).
   * Runs when <Form> initializes with/without an entity, i.e. Create/Edit mode,
   * and also during the submit process, before payload is sent to the server.
   * @param {import('../shared/components/FormV2/types').ProcessEntityOptions} options
   * @returns {Object} The transformed entity object.
   */
  const processEntity = useCallback(
    ({ entity, mode = 'fromServer', ...fieldsOptions }) => {
      const fields = getFields(fieldsOptions);
      const values = pick(
        entity,
        fields.map((f) => f.name),
        /_data_ent_name_map$/
      );
      const result = fields.reduce((acc, field) => {
        const value = values[field.name];
        const FormComponent = fieldTypeToComponentMap[field.fieldType];
        const val = FormComponent.transform[mode](value, {
          field: { ...field, entityType: fieldsOptions.entityType },
          payload: values,
        });
        return {
          ...acc,
          ...(isNonArrayObject(val) && isNotEmpty(val)
            ? // Transform functions can return objects with different key names than the field name
              // (e.g., tag → tag_values, additional_customers → additional_customer_ids).
              // This gives form components full control over value transformation.
              val
            : // Otherwise, we are dealing with a simple transformed value,
              // so use the field name as the key.
              { [field.name]: isNil(value) ? value : val }),
        };
      }, {});
      return result;
    },
    [getFields]
  );

  const contextValue = useMemo(
    () => ({
      getFields,
      processEntity,
      getDefaultValues,
      getAttachmentEntities,
    }),
    [getFields, getDefaultValues, getAttachmentEntities, processEntity]
  );

  useEffect(() => {
    moduleStore = contextValue;
  }, [contextValue]);

  return (
    <FieldContext.Provider value={{ ...contextValue, isLoading }}>
      {!moduleStore ? (
        <Loading dim fullScreen />
      ) : (
        children
      )}
    </FieldContext.Provider>
  );
}

function useFieldConfig() {
  const context = useContext(FieldContext);
  if (context === undefined) {
    throw new Error('useFieldConfig must be used within a FieldProvider');
  }
  return context;
}

/**
 * Provides access to context-level field management utilities.
 * @type {React.Context<import('../shared/components/FormV2/types').FieldContextValue>}
 */
const FieldContext = React.createContext(undefined);
FieldContext.displayName = 'FieldContext';

export const getFields = (...args) => moduleStore?.getFields(...args);
export const processEntity = (...args) => moduleStore?.processEntity(...args);
export const getDefaultValues = (...args) => moduleStore?.getDefaultValues(...args);
export { FieldProvider, useFieldConfig };
